import { noCase } from "change-case";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// @mui
import {
  Box,
  Stack,
  List,
  Badge,
  Avatar,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  Button,
  CircularProgress,
  Chip,
  IconButton,
} from "@mui/material";
// utils
import { fToNow } from "../../../utils/formatTime";
import moment from "moment";
// components
import Iconify from "../../../components/iconify";
import Scrollbar from "../../../components/scrollbar";
import MenuPopover from "../../../components/menu-popover";
import { IconButtonAnimate } from "../../../components/animate";
import { PRIMARY } from "../../../theme/palette";
import WebSocketStatus from "../../../components/notifications/WebSocketStatus";
import NotificationPreferences from "../../../components/notifications/NotificationPreferences";
// hooks
import { useNotifications, useUpdateNotificationStatus, useUnreadCount, useNotificationSubscription } from "../../../hooks/useNotifications";
import { useAuthContext } from "../../../sections/auth/useAuthContext";
import { handleNotificationNavigation } from "../../../utils/notificationNavigation";

// ----------------------------------------------------------------------

export default function NotificationsPopover() {
  const [openPopover, setOpenPopover] = useState(null);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuthContext();
  
  // Get recent notifications (last 5) for current user
  const { data: notificationsData, loading: isLoading } = useNotifications({ 
    userId: { id: { equals: user?.id } }
  });
  
  const notifications = notificationsData?.notificationsList?.items || [];
  
  // Get unread count for current user
  const { data: unreadCountData } = useUnreadCount(user?.id);
  const unreadCount = unreadCountData?.notificationsList?.count || 0;
  
  const [updateStatusMutation] = useUpdateNotificationStatus();
  
  // 🔔 Real-time WebSocket subscription for instant notifications
  const { 
    data: subscriptionData, 
    loading: subscriptionLoading, 
    error: subscriptionError,
    isConnected: subscriptionConnected 
  } = useNotificationSubscription(user?.id);
  
  // Log subscription status
  useEffect(() => {
    if (user?.id) {
      console.log('✅ Real-time notifications active for:', user.id);
      if (subscriptionError) {
        console.error('❌ WebSocket subscription error:', subscriptionError.message);
      }
      if (subscriptionConnected) {
        console.log('🎉 WebSocket subscription ready and listening!');
      }
    }
  }, [user?.id, subscriptionConnected, subscriptionError]);
  
  const handleOpenPopover = (event) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };
  
  const handleViewAll = () => {
    navigate('/notifications');
    handleClosePopover();
  };
  
  const handleNotificationClick = (notification) => {
    // Mark as read in background (don't wait for it)
    if (notification.status === 'UNREAD') {
      updateStatusMutation({
        variables: { 
          id: notification.id, 
          status: 'READ',
          clearedAt: null
        }
        // Removed refetchQueries to prevent double refresh
        // The real-time WebSocket subscription will handle UI updates automatically
      });
    }
    
    // Close popover
    handleClosePopover();
    
    // Use smart navigation based on jsonRecord
    const navigated = handleNotificationNavigation(notification, navigate);
    
    // If smart navigation didn't work, fallback to relatedEntityUrl or notifications page
    if (!navigated) {
      if (notification.relatedEntityUrl) {
        const url = notification.relatedEntityUrl;
        const path = url.startsWith('http') ? new URL(url).pathname : url;
        navigate(path);
      } else {
        // Fallback to notification list page if no navigation path is available
        navigate('/notifications');
      }
    }
  };
  
  // Sort notifications: UNREAD first, then READ, then CLEARED, with newest first within each group
  const sortedNotifications = [...notifications].sort((a, b) => {
    // Define status priority order: UNREAD (0), READ (1), CLEARED (2)
    const statusPriority = {
      'UNREAD': 0,
      'READ': 1,
      'CLEARED': 2
    };
    
    const aPriority = statusPriority[a.status] || 3;
    const bPriority = statusPriority[b.status] || 3;
    
    // First, sort by status priority (UNREAD → READ → CLEARED)
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }
    
    // Then sort by creation date (newest first within same status)
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  
  const recentNotifications = sortedNotifications.slice(0, 5);

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <IconButtonAnimate
          color={openPopover ? "default" : "default"}
          onClick={handleOpenPopover}
          sx={{ width: 40, height: 40, background: `#fff !important` }}
        >
          <Badge color="error" badgeContent={unreadCount} max={99}>
            <Iconify
              icon="mdi:bell"
              sx={{ width: 30, height: 30 }}
              color="#637381"
            />
          </Badge>
        </IconButtonAnimate>
        {/* WebSocket Connection Status Indicator */}
        <Box sx={{ 
          position: 'absolute', 
          bottom: 2, 
          right: 2,
          zIndex: 1
        }}>
          <WebSocketStatus 
            loading={subscriptionLoading} 
            error={subscriptionError}
            isConnected={subscriptionConnected}
          />
        </Box>
      </Box>

      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        sx={{ width: 360, p: 0 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" color="text.secondary">
                {unreadCount} unread
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={() => {
                setOpenPopover(null);
                setPreferencesOpen(true);
              }}
              sx={{ color: 'text.secondary' }}
            >
              <Iconify icon="eva:settings-2-fill" />
            </IconButton>
            <Button size="small" onClick={handleViewAll}>
              View All
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ borderStyle: "dashed" }} />

        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentNotifications.length > 0 ? (
            <List disablePadding>
              {recentNotifications.map((notification) => (
                <NotificationItem 
                  key={notification.id} 
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                />
              ))}
            </List>
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No notifications yet
              </Typography>
            </Box>
          )}
        </Scrollbar>
        
        {recentNotifications.length > 0 && (
          <>
            <Divider sx={{ borderStyle: "dashed" }} />
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button size="small" onClick={handleViewAll}>
                View All Notifications
              </Button>
            </Box>
          </>
        )}
      </MenuPopover>
      
      <NotificationPreferences 
        open={preferencesOpen} 
        onClose={() => setPreferencesOpen(false)} 
      />
    </>
  );
}

// ----------------------------------------------------------------------

function NotificationItem({ notification, onClick }) {
  const getStatusIcon = () => {
    switch (notification.status) {
      case 'UNREAD':
        return <Iconify icon="eva:radio-button-on-fill" width={16} color="primary" />;
      case 'READ':
        return <Iconify icon="eva:radio-button-off-fill" width={16} color="text.disabled" />;
      case 'CLEARED':
        return <Iconify icon="eva:checkmark-circle-2-fill" width={16} color="text.disabled" />;
      default:
        return <Iconify icon="eva:radio-button-off-fill" width={16} color="text.disabled" />;
    }
  };
  
  // Parse jsonRecord to extract schedule details
  const getScheduleDetails = () => {
    if (!notification.jsonRecord) return null;
    
    try {
      const jsonRecord = typeof notification.jsonRecord === 'string' 
        ? JSON.parse(notification.jsonRecord) 
        : notification.jsonRecord;
      
      if (jsonRecord.notificationType === 'schedule') {
        const schedule = jsonRecord.schedule || jsonRecord;
        const startTime = schedule.timeStart || schedule.startTime;
        const endTime = schedule.timeEnd || schedule.endTime;
        const totalHours = schedule.totalHours;
        
        return { startTime, endTime, totalHours };
      }
    } catch (e) {
      // Silent fail for popover
    }
    
    return null;
  };
  
  const scheduleDetails = getScheduleDetails();
  
  // Format time for display
  const formatTime = (time) => {
    if (!time) return null;
    try {
      const timeMoment = moment(time);
      if (timeMoment.isValid()) {
        return timeMoment.format('h:mm A');
      }
      return time;
    } catch (e) {
      return time;
    }
  };

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: "1px",
        ...(notification.status === 'UNREAD' && {
          bgcolor: "action.selected",
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: "background.neutral", width: 32, height: 32 }}>
          {getStatusIcon()}
        </Avatar>
      </ListItemAvatar>

      <ListItemText
        primary={
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: notification.status === 'UNREAD' ? 'bold' : 'normal',
              fontSize: '0.875rem'
            }}
          >
            {notification.title}
          </Typography>
        }
        secondary={
          <Stack
            direction="column"
            sx={{ mt: 0.5 }}
          >
            {notification.description && (
              <Typography 
                variant="caption" 
                color="text.secondary"
                sx={{ 
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  mb: 0.5
                }}
              >
                {notification.description}
              </Typography>
            )}
            {/* Schedule Details - Compact display for popover */}
            {scheduleDetails && (scheduleDetails.startTime || scheduleDetails.endTime || scheduleDetails.totalHours) && (
              <Stack direction="row" spacing={0.5} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                {scheduleDetails.startTime && scheduleDetails.endTime && (
                  <Chip
                    label={`${formatTime(scheduleDetails.startTime)} - ${formatTime(scheduleDetails.endTime)}`}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.65rem',
                      bgcolor: 'primary.lighter',
                      color: 'primary.darker',
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                )}
                {scheduleDetails.totalHours && (
                  <Chip
                    label={`${scheduleDetails.totalHours} hrs`}
                    size="small"
                    sx={{
                      height: 16,
                      fontSize: '0.65rem',
                      bgcolor: 'warning.lighter',
                      color: 'warning.darker',
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                )}
              </Stack>
            )}
            <Stack
              direction="row"
              sx={{ typography: "caption", color: "text.disabled" }}
            >
              <Iconify icon="eva:clock-fill" width={12} sx={{ mr: 0.5 }} />
              <Typography variant="caption">
                {fToNow(notification.createdAt)}
              </Typography>
            </Stack>
          </Stack>
        }
      />
    </ListItemButton>
  );
}

